import { useState, useRef, useEffect } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { csrfFetch } from "../lib/csrfFetch";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  PaperPlaneTilt, SpinnerGap, VideoCamera, Clock, Robot, User, Sparkle,
  ArrowClockwise, Trash, Copy, Check, ArrowsOutSimple, ArrowsInSimple,
  ThumbsUp, ThumbsDown, X, ArrowRight, CaretDown, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";
const EMPTY_STATE = "https://static.prod-images.emergentagent.com/jobs/9ce68c36-1b03-4868-a37f-38db73ced2af/images/ac1e39a43e42bf5a6e74d582c502572ddd5e50b14cd865b9eb668efafb6c961d.png";

// Vincent #261 (2026-02-06) — Composants markdown extraits hors du
// ChatPage pour éviter "react/no-unstable-nested-components" : ces
// composants sont stables car ils ne capturent aucune valeur du state.
const MdLink = ({ href, children, ...props }) => {
  // Vincent #260 (2026-02-06) — Liens vers /dashboard/quiz?topic=... rendus
  // avec une icône quiz proéminente pour inciter à cliquer.
  const isQuizLink = typeof href === "string" && href.startsWith("/dashboard/quiz");
  if (isQuizLink) {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 mt-2 rounded-lg bg-gradient-to-r from-violet-50 to-purple-50 text-violet-700 border border-violet-200 hover:border-violet-400 hover:bg-violet-50 transition-colors font-semibold text-xs"
        data-testid="chatbot-quiz-link"
        {...props}
      >
        🎯 {children}
      </Link>
    );
  }
  const internalMatch = typeof href === "string" && href.match(/^\/dashboard\/videos\/([^?#]+)/);
  if (internalMatch) {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1 text-[#002FA7] hover:underline font-medium"
        {...props}
      >
        <VideoCamera size={12} /> {children}
      </Link>
    );
  }
  // Autres liens /dashboard/* → React Router
  if (typeof href === "string" && href.startsWith("/dashboard/")) {
    return (
      <Link
        to={href}
        className="text-[#002FA7] hover:underline font-medium"
        {...props}
      >
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#002FA7] hover:underline font-medium"
      {...props}
    >
      {children}
    </a>
  );
};
const MdUl = ({ children, ...props }) => <ul className="list-disc ml-5 my-1.5 space-y-0.5" {...props}>{children}</ul>;
const MdOl = ({ children, ...props }) => <ol className="list-decimal ml-5 my-1.5 space-y-0.5" {...props}>{children}</ol>;
const MdLi = ({ children, ...props }) => <li className="leading-snug" {...props}>{children}</li>;
const MdStrong = ({ children, ...props }) => <strong className="font-semibold text-zinc-900" {...props}>{children}</strong>;
const MdEm = ({ children, ...props }) => <em className="italic" {...props}>{children}</em>;
const MdH1 = ({ children, ...props }) => <h3 className="font-semibold text-base mt-2 mb-1" {...props}>{children}</h3>;
const MdH2 = ({ children, ...props }) => <h3 className="font-semibold text-sm mt-2 mb-1" {...props}>{children}</h3>;
const MdH3 = ({ children, ...props }) => <h4 className="font-semibold text-sm mt-1.5 mb-1" {...props}>{children}</h4>;
const MdP = ({ children, ...props }) => <p className="my-1 leading-relaxed" {...props}>{children}</p>;
const MdCode = ({ inline, children, ...props }) => (
  inline
    ? <code className="px-1 py-0.5 bg-zinc-100 text-rose-700 rounded text-[0.85em] font-mono" {...props}>{children}</code>
    : <code className="block px-3 py-2 bg-zinc-100 text-zinc-800 rounded-md text-[0.85em] font-mono overflow-x-auto my-1.5" {...props}>{children}</code>
);
const MdBlockquote = ({ children, ...props }) => <blockquote className="border-l-2 border-zinc-300 pl-3 my-1.5 text-zinc-600 italic" {...props}>{children}</blockquote>;

const MARKDOWN_COMPONENTS = {
  a: MdLink,
  ul: MdUl,
  ol: MdOl,
  li: MdLi,
  strong: MdStrong,
  em: MdEm,
  h1: MdH1,
  h2: MdH2,
  h3: MdH3,
  p: MdP,
  code: MdCode,
  blockquote: MdBlockquote,
};

export default function ChatPage() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [filterVideo, setFilterVideo] = useState("");
  // Vincent (2026-07-22) — Tickets 515/517 : remplace l'ancien filtre
  // "Tous les modules" (training_theme) par 2 nouveaux filtres UI :
  //   - filterPathway : parcours (onboarding + learning, groupés + tri alpha)
  //   - filterDocCategory : catégories de documents (content_categories, tri alpha)
  // Ces filtres restreignent les vidéos affichées dans le dropdown vidéos.
  const [filterPathway, setFilterPathway] = useState("");
  const [filterDocCategory, setFilterDocCategory] = useState("");
  const [videos, setVideos] = useState([]);
  const [pathways, setPathways] = useState([]);
  const [docCategories, setDocCategories] = useState([]);
  // Vincent (2026-07-22) — Ticket 519 : historique persisté + menu navigation
  const [showHistoryMenu, setShowHistoryMenu] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const messagesRefs = useRef({}); // {msgIdx: DOM node} pour scroll vers question précédente
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Load videos, pathways, and doc categories for filtering
    axios.get(`${API}/api/videos`, { withCredentials: true }).then(r => {
      const vids = r.data?.items || r.data?.videos || r.data || [];
      setVideos(Array.isArray(vids) ? vids : []);
    }).catch(() => {});
    // Vincent (2026-07-22) — Ticket 515 : charger les parcours pour le
    // nouveau filtre "Tous les parcours" (groupé onboarding/learning + tri).
    axios.get(`${API}/api/pathways`, { withCredentials: true }).then(r => {
      const arr = r.data?.pathways || r.data?.items || r.data || [];
      setPathways(Array.isArray(arr) ? arr : []);
    }).catch(() => setPathways([]));
    // Vincent (2026-07-22) — Ticket 517 : charger les catégories de
    // documents (content_categories) pour le nouveau filtre.
    axios.get(`${API}/api/content-categories`, { withCredentials: true }).then(r => {
      const arr = r.data?.categories || r.data?.items || r.data || [];
      setDocCategories(Array.isArray(arr) ? arr : []);
    }).catch(() => setDocCategories([]));
    // Vincent (2026-07-22) — Ticket 519 : Restaurer l'historique des
    // questions/réponses de l'utilisateur au chargement de la page. Avant,
    // les `messages` étaient purement locaux et se perdaient à chaque
    // navigation. Maintenant, on hydrate depuis /chat/history (backend
    // filtre les queries masquées via hidden_by_user).
    axios.get(`${API}/api/chat/history?limit=50`, { withCredentials: true }).then(r => {
      const queries = Array.isArray(r.data) ? r.data : [];
      // /chat/history retourne les plus récents en premier → inverse pour
      // afficher chronologiquement (ancien → nouveau) dans le fil de discussion.
      const restored = [];
      for (const q of queries.slice().reverse()) {
        if (q.question) restored.push({ role: "user", content: q.question, timestamp: q.created_at });
        if (q.answer) restored.push({
          role: "assistant", content: q.answer,
          sources: q.sources, related_questions: q.related_questions,
          query_id: q.id, feedback: q.feedback, feedback_reason: q.feedback_reason,
          timestamp: q.created_at,
        });
      }
      if (restored.length > 0) setMessages(restored);
    }).catch(() => {});
  }, []);

  const SUGGESTED_QUESTIONS = lang === "fr" ? [
    "Quel est le processus d'intégration d'un nouveau client?",
    "Comment gérer les objections sur les frais?",
    "Quelles sont les exigences de conformité?",
    "Expliquer le processus de l'énoncé de politique de placement",
  ] : [
    "What is the client onboarding process?",
    "How do I handle objections about fees?",
    "What are the compliance requirements for new accounts?",
    "Explain the investment policy statement process",
  ];

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape" && fullscreen) setFullscreen(false); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [fullscreen]);

  const handleSubmit = async (questionOverride) => {
    const q = questionOverride || input;
    if (!q?.trim() || loading) return;
    const userMsg = { role: "user", content: q.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    // Vincent B606 optim D (Eric 2026-07-29 SPEC STRICK) — Streaming SSE.
    // Bascule sur /api/chat/stream : le message assistant est ajouté
    // immédiatement (vide), puis on parse le flux event-stream ligne par
    // ligne. Le TTFB perçu passe de 3-8 s à ~500-800 ms (temps prep +
    // 1er token GPT). Fallback silencieux vers l'ancien /api/chat en
    // cas d'erreur réseau (rétrocompat pour anciens navigateurs et
    // proxys sans support streaming).
    const assistantIdx = messages.length + 1; // index dans le NOUVEAU tableau (user + assistant)
    const placeholderMsg = {
      role: "assistant",
      content: "",
      sources: [],
      related_questions: [],
      query_id: null,
      timestamp: new Date().toISOString(),
      streaming: true,
    };
    setMessages(prev => [...prev, placeholderMsg]);

    try {
      const body = {
        message: q.trim(),
        session_id: `chat-${user?.id}`,
        ...(filterVideo ? { video_id: filterVideo } : {}),
      };
      // Vincent B651 (Eric 2026-08-05 SPEC STRICK PROD) — csrfFetch injecte
      // le header X-XSRF-TOKEN nécessaire pour tous les POST (axios le fait
      // via interceptor, mais fetch nécessite l'ajout manuel). Sans ce
      // header, le middleware CSRF backend retournait 403 → frontend
      // affichait « Désolé, une erreur est survenue ».
      const resp = await csrfFetch(`${API}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let streamedText = "";

      const applyEvent = (evt) => {
        if (evt.type === "meta") {
          // Sources arrivent AVANT le 1er token GPT — on les affiche
          // en amont pour améliorer la perception UX.
          setMessages(prev => prev.map((m, i) => i === assistantIdx
            ? { ...m, sources: evt.sources || [], query_id: evt.query_id }
            : m));
        } else if (evt.type === "chunk") {
          streamedText += evt.text || "";
          // Update the streamed text incrementally.
          setMessages(prev => prev.map((m, i) => i === assistantIdx
            ? { ...m, content: streamedText }
            : m));
        } else if (evt.type === "done") {
          // Remplace le buffer streamé par la version FINALE sanitizée
          // (URLs invalides retirées, sources dédupliquées, etc.).
          setMessages(prev => prev.map((m, i) => i === assistantIdx ? {
            ...m,
            content: evt.answer_final || streamedText,
            sources: evt.sources || m.sources || [],
            related_questions: evt.related_questions || [],
            query_id: evt.query_id || m.query_id,
            streaming: false,
          } : m));
        } else if (evt.type === "error") {
          setMessages(prev => prev.map((m, i) => i === assistantIdx ? {
            ...m,
            content: evt.message || (lang === "fr" ? "Désolé, une erreur est survenue." : "Sorry, an error occurred."),
            streaming: false,
          } : m));
        }
      };

      // Read the SSE stream chunk by chunk. Each event is `data: <json>\n\n`.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Split on double newline (SSE event delimiter).
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || ""; // keep last incomplete chunk
        for (const raw of parts) {
          const line = raw.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            applyEvent(evt);
          } catch (_) { /* ignore malformed event */ }
        }
      }
      // Flush any remaining buffered event.
      if (buffer.trim().startsWith("data:")) {
        try {
          const evt = JSON.parse(buffer.trim().slice(5).trim());
          applyEvent(evt);
        } catch (_) { /* ignore */ }
      }
      // Safety : if the stream ended without a `done` event, clear the
      // streaming flag anyway so the copy button / feedback appear.
      setMessages(prev => prev.map((m, i) => i === assistantIdx ? { ...m, streaming: false } : m));
    } catch {
      setMessages(prev => prev.map((m, i) => i === assistantIdx ? {
        ...m,
        content: lang === "fr" ? "Désolé, une erreur est survenue." : "Sorry, an error occurred.",
        streaming: false,
      } : m));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleCopy = (content, id) => { navigator.clipboard.writeText(content); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); };

  // Vincent #509 (2026-07-21) — Refonte du feedback chatbot :
  // - retirer les boutons Question ("unclear"), X ("off_topic"), LinkBreak
  //   ("broken_link")
  // - garder ThumbsUp ("good")
  // - ajouter ThumbsDown ouvrant un menu de 7 raisons pour former le
  //   dialogueur et informer les concepteurs.
  const [feedbackMenu, setFeedbackMenu] = useState(null); // { msgIdx } ouvert
  const [customReasonText, setCustomReasonText] = useState("");
  const FEEDBACK_DOWN_REASONS = [
    { id: "factually_false", label: "Information factuellement fausse", desc: "L'IA affirme quelque chose de contraire à la réalité." },
    { id: "hallucination", label: "Hallucination (invention)", desc: "L'IA a inventé des noms, des dates ou des faits qui n'existent pas." },
    { id: "outdated", label: "Donnée obsolète", desc: "La réponse était vraie par le passé, mais n'est plus à jour." },
    { id: "internal_contradiction", label: "Contradiction interne", desc: "L'IA dit une chose, puis son contraire, dans la même réponse." },
    { id: "misinterprets_source", label: "Mauvaise interprétation de la source", desc: "L'IA cite un document existant mais en tire une conclusion erronée." },
    { id: "invalid_link", label: "Lien ou référence invalide", desc: "La source citée est inaccessible ou ne contient pas l'information mentionnée." },
    { id: "other", label: "Autre", desc: "Précise ta raison dans un commentaire." },
  ];

  const submitFeedback = async (msgIdx, rating, extra = {}) => {
    const msg = messages[msgIdx];
    if (!msg?.query_id || msg.feedback) return;
    // Vincent (2026-07-22) — Ticket 514 : Optimistic update pour que le
    // pouce change de couleur + devienne plein IMMÉDIATEMENT au clic
    // (avant, un rollback silencieux repositionnait le pouce en cas
    // d'échec réseau, sans que l'utilisateur voie le feedback visuel).
    setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, feedback: rating, feedback_reason: extra.reason || null } : m));
    try {
      const payload = { query_id: msg.query_id, rating };
      if (extra.reason) payload.reason = extra.reason;
      if (extra.comment) payload.comment = extra.comment;
      await axios.post(`${API}/api/chat/feedback`, payload, { withCredentials: true });
      const successMsg =
        rating === "good"
          ? (lang === "fr" ? "Merci pour votre retour !" : "Thanks for your feedback!")
          : (lang === "fr" ? "Merci — ton retour va aider à améliorer les réponses." : "Thanks — your feedback will help improve responses.");
      toast.success(successMsg);
    } catch (e) {
      // Vincent (2026-07-22) — Ticket 514 : Message d'erreur explicite au
      // lieu du "Erreur" vague. On garde le pouce plein 1.5s avant
      // rollback pour que l'utilisateur voie l'intention de son clic, puis
      // on informe précisément de ce qui s'est passé.
      const detail = e?.response?.data?.detail;
      const httpStatus = e?.response?.status;
      let errorMsg;
      if (httpStatus === 404) {
        errorMsg = lang === "fr" ? "Cette réponse n'existe plus en base — impossible d'enregistrer le retour." : "This answer no longer exists — cannot save feedback.";
      } else if (httpStatus === 400 && detail) {
        errorMsg = lang === "fr" ? `Retour invalide : ${detail}` : `Invalid feedback: ${detail}`;
      } else if (httpStatus === 401 || httpStatus === 403) {
        errorMsg = lang === "fr" ? "Session expirée — reconnecte-toi pour envoyer ton retour." : "Session expired — sign in again to send feedback.";
      } else {
        errorMsg = lang === "fr"
          ? `Impossible d'enregistrer ton retour${detail ? ` (${detail})` : ""} — vérifie ta connexion internet.`
          : `Cannot save feedback${detail ? ` (${detail})` : ""} — check your internet connection.`;
      }
      toast.error(errorMsg);
      // Rollback léger décalé pour laisser le pouce plein visible ~1.5s
      setTimeout(() => {
        setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, feedback: undefined, feedback_reason: undefined } : m));
      }, 1500);
    }
  };

  const formatTs = (s) => { if (!s) return ""; return `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`; };

  // Vincent #261 (2026-02-06) — Avant : on affichait le texte brut, donc les
  // marqueurs Markdown du LLM (`**gras**`, `- listes`, `### titres`) restaient
  // visibles littéralement. Maintenant on parse avec react-markdown + remark-gfm
  // pour rendre du HTML lisible (gras, italique, listes, titres, code).
  // Les composants stables sont définis hors composant (cf. MARKDOWN_COMPONENTS).
  const renderContent = (text) => {
    if (!text) return text;
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
        {text}
      </ReactMarkdown>
    );
  };

  const containerClass = fullscreen
    ? "fixed inset-0 z-50 bg-white flex flex-col p-4 lg:p-8 animate-fade-in"
    : "flex flex-col h-[calc(100vh-8rem)] animate-fade-in";

  return (
    <div className={containerClass} data-testid="chat-page">
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div>
          <h1 className="text-xl lg:text-2xl font-semibold tracking-tight font-['Outfit']">{t("chat_title")}</h1>
          {!fullscreen && <p className="text-sm text-zinc-500 mt-0.5">{t("chat_subtitle")}</p>}
        </div>
        <div className="flex items-center gap-2">
          {/* Vincent (2026-07-22) — Ticket 519 : Menu déroulant "Historique"
              qui liste les questions posées précédemment et permet de
              scroller directement vers la question dans le fil. */}
          {messages.length > 0 && (
            <div className="relative">
              <Button
                variant="outline" size="sm"
                onClick={() => setShowHistoryMenu(!showHistoryMenu)}
                className="gap-1.5 text-zinc-600"
                data-testid="chat-history-menu-btn"
              >
                <ClockCounterClockwise size={14} />
                {!fullscreen && (lang === "fr" ? "Historique" : "History")}
                <CaretDown size={11} />
              </Button>
              {showHistoryMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowHistoryMenu(false)} />
                  <div
                    className="absolute right-0 top-full mt-1 w-80 max-h-96 overflow-y-auto bg-white border border-zinc-200 rounded-lg shadow-xl z-40"
                    data-testid="chat-history-menu"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                      {lang === "fr" ? "Questions précédentes" : "Previous questions"}
                    </div>
                    {messages.filter(m => m.role === "user").length === 0 && (
                      <div className="px-3 py-4 text-xs text-zinc-400 text-center">
                        {lang === "fr" ? "Aucune question encore" : "No questions yet"}
                      </div>
                    )}
                    {messages.map((m, i) => m.role === "user" ? (
                      <button
                        key={i}
                        onClick={() => {
                          const el = messagesRefs.current[i];
                          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                          setShowHistoryMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-b-0 group"
                        data-testid={`history-item-${i}`}
                      >
                        <p className="text-xs text-zinc-700 line-clamp-2 group-hover:text-[#002FA7]">
                          {m.content?.substring(0, 140)}{m.content?.length > 140 ? "…" : ""}
                        </p>
                        {m.timestamp && (
                          <p className="text-[10px] text-zinc-400 mt-1">
                            {new Date(m.timestamp).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </p>
                        )}
                      </button>
                    ) : null)}
                  </div>
                </>
              )}
            </div>
          )}
          {messages.length > 0 && (
            <Button
              variant="outline" size="sm"
              onClick={() => setShowClearConfirm(true)}
              className="gap-1.5 text-zinc-500"
              data-testid="clear-chat-btn"
            >
              <Trash size={14} /> {!fullscreen && t("delete")}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setFullscreen(!fullscreen)} className="gap-1.5 text-zinc-500" data-testid="fullscreen-chat-btn">
            {fullscreen ? <><ArrowsInSimple size={14} /> {lang === "fr" ? "Réduire" : "Minimize"}</> : <><ArrowsOutSimple size={14} /> {lang === "fr" ? "Plein écran" : "Fullscreen"}</>}
          </Button>
        </div>
      </div>

      {/* Vincent (2026-07-22) — Ticket 519 : Modal de confirmation avant
          effacement de l'historique. Précise que l'historique reste
          accessible côté serveur pour l'entrainement du chatbot. */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowClearConfirm(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="clear-history-confirm"
          >
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              {lang === "fr" ? "Effacer l'historique affiché ?" : "Clear displayed history?"}
            </h3>
            <p className="text-sm text-zinc-600 mb-4">
              {lang === "fr"
                ? "Pour des raisons d'entrainement du chatbot et de débogage, votre historique de questions/réponses restera accessible au chatbot mais ne sera plus affiché ici. Cette action ne peut pas être annulée depuis l'interface."
                : "For chatbot training and debugging purposes, your question/answer history will remain accessible to the chatbot but will no longer be displayed here. This action cannot be undone from the UI."}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowClearConfirm(false)} data-testid="clear-history-cancel">
                {lang === "fr" ? "Annuler" : "Cancel"}
              </Button>
              <Button
                variant="destructive" size="sm"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/api/chat/history/hide`, {}, { withCredentials: true });
                    setMessages([]);
                    setShowClearConfirm(false);
                    toast.success(lang === "fr" ? "Historique effacé de l'affichage." : "History cleared from display.");
                  } catch (e) {
                    toast.error(lang === "fr" ? "Erreur lors de l'effacement." : "Error clearing history.");
                  }
                }}
                data-testid="clear-history-confirm-btn"
              >
                <Trash size={14} className="mr-1.5" />
                {lang === "fr" ? "Effacer l'affichage" : "Clear display"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vincent (2026-07-22) — Tickets 515/516/517 : Filtres refondus
          Parcours (onboarding puis learning, séparés par un trait) +
          Catégories de documents (tri alpha) + Vidéos (tri alpha). */}
      <div className="flex items-center gap-2 mb-2 flex-shrink-0 flex-wrap" data-testid="chat-filters">
        <select
          value={filterPathway}
          onChange={(e) => { setFilterPathway(e.target.value); setFilterVideo(""); }}
          className="h-8 px-2 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-600"
          data-testid="filter-pathway"
        >
          <option value="">{lang === "fr" ? "Tous les parcours" : "All pathways"}</option>
          {(() => {
            const onboarding = pathways.filter(p => p.pathway_type === "onboarding").sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
            const learning = pathways.filter(p => p.pathway_type === "learning").sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
            const weekly = pathways.filter(p => p.pathway_type === "weekly").sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
            return (
              <>
                {onboarding.length > 0 && (
                  <optgroup label={lang === "fr" ? "── Parcours d'intégration ──" : "── Onboarding pathways ──"}>
                    {onboarding.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                )}
                {learning.length > 0 && (
                  <optgroup label={lang === "fr" ? "── Parcours d'apprentissage ──" : "── Learning pathways ──"}>
                    {learning.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                )}
                {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) */}
                {weekly.length > 0 && (
                  <optgroup label={lang === "fr" ? "── Formations hebdomadaires ──" : "── Weekly trainings ──"}>
                    {weekly.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                )}
              </>
            );
          })()}
        </select>
        <select
          value={filterDocCategory}
          onChange={(e) => { setFilterDocCategory(e.target.value); setFilterVideo(""); }}
          className="h-8 px-2 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-600"
          data-testid="filter-doc-category"
        >
          <option value="">{lang === "fr" ? "Toutes les catégories" : "All categories"}</option>
          {[...docCategories]
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }))
            .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={filterVideo}
          onChange={(e) => setFilterVideo(e.target.value)}
          className="h-8 px-2 text-xs border border-zinc-200 rounded-lg bg-white text-zinc-600 max-w-[250px]"
          data-testid="filter-video"
        >
          <option value="">{lang === "fr" ? "Toutes les vidéos" : "All videos"}</option>
          {videos
            .filter(v => {
              // Ticket 515 : filtre par parcours (via pathway_ids ou pathway_id sur la vidéo)
              if (filterPathway) {
                const pathwayIds = Array.isArray(v.pathway_ids) ? v.pathway_ids : [];
                const singleId = v.pathway_id;
                if (!pathwayIds.includes(filterPathway) && singleId !== filterPathway) return false;
              }
              // Ticket 517 : filtre par catégorie de documents
              if (filterDocCategory && v.category_id !== filterDocCategory) return false;
              return true;
            })
            // Ticket 516 : tri alphabétique croissant des vidéos
            .sort((a, b) => (a.title || "").localeCompare(b.title || "", "fr", { sensitivity: "base" }))
            .map(v => <option key={v.id} value={v.id}>{v.title?.substring(0, 50)}</option>)}
        </select>
        {(filterVideo || filterPathway || filterDocCategory) && (
          <button
            onClick={() => { setFilterVideo(""); setFilterPathway(""); setFilterDocCategory(""); }}
            className="h-8 px-2 text-xs text-red-500 hover:bg-red-50 rounded-lg border border-red-200 flex items-center gap-1"
            data-testid="clear-filters"
          >
            <ArrowClockwise size={12} /> {lang === "fr" ? "Tout" : "All"}
          </button>
        )}
        {(filterVideo || filterPathway || filterDocCategory) && (
          <span className="text-[10px] text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
            {lang === "fr" ? "Filtre actif" : "Filter active"}
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto bg-white border border-zinc-200 rounded-xl min-h-0">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center p-8 text-center">
            <img src={EMPTY_STATE} alt="" className="w-32 h-32 mb-6 opacity-60" />
            <h3 className="text-lg font-medium font-['Outfit']">{t("chat_title")}</h3>
            <p className="text-zinc-500 mt-2 max-w-md text-sm">{t("start_asking")}</p>
            <div className="mt-6 w-full max-w-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button key={i} onClick={() => handleSubmit(q)} className="text-left text-sm p-3 bg-zinc-50 border border-zinc-200 rounded-lg hover:bg-zinc-100 hover:border-[#002FA7]/30 transition-colors" data-testid={`suggestion-${i}`}>{q}</button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 lg:p-6 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                ref={(el) => { if (el) messagesRefs.current[i] = el; }}
                className={`flex items-start gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === "user" ? "bg-zinc-200" : "bg-[#002FA7]"}`}>
                  {msg.role === "user" ? <User size={16} className="text-zinc-600" /> : <Robot size={16} className="text-white" />}
                </div>
                <div className={`flex-1 rounded-xl p-4 ${msg.role === "user" ? "bg-[#002FA7] text-white max-w-[75%] ml-auto" : "bg-zinc-50 border border-zinc-200"} ${fullscreen ? "max-w-4xl" : ""}`}>
                  <div className={`text-sm leading-relaxed ${msg.role === "user" ? "whitespace-pre-wrap" : "text-zinc-800"}`} data-testid={msg.streaming ? `chat-msg-streaming-${i}` : `chat-msg-${i}`}>
                    {msg.role === "user" ? msg.content : renderContent(msg.content)}
                    {/* Vincent B606 optim D (2026-07-29) — Curseur clignotant
                        pendant le streaming SSE pour signaler visuellement
                        que la réponse se compose token-par-token. */}
                    {msg.streaming && (
                      <span className="inline-block w-2 h-4 ml-1 align-middle bg-[#002FA7] animate-pulse" data-testid={`chat-streaming-cursor-${i}`} aria-hidden="true" />
                    )}
                  </div>
                  {(() => {
                    // Bug 532 (Eric 2026-02-14) — Filtre défensif : n'affiche que
                    // les sources ayant un `video_id` ET un `video_title` non-vides.
                    // Les sources de type channel_message / dm_message (rag.py
                    // lignes 152/183) n'ont ni l'un ni l'autre → apparaissaient
                    // comme des lignes vides sous « Sources » (icône vidéo + span
                    // vide). Récurrence du bug 526.
                    const visibleSources = (msg.sources || []).filter(
                      (s) => s && s.video_id && s.video_title && String(s.video_title).trim()
                    );
                    return visibleSources.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-200">
                      <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1"><Sparkle size={12} /> {t("sources")}</p>
                      <div className="space-y-1.5">
                        {visibleSources.map((src, si) => {
                          const startSec = src.timestamps?.start;
                          // Vincent B709 (Eric 2026-02-11) — La source la plus
                          // pertinente (index 0) est mise en gras pour signaler
                          // « source principale » sans doublon d'affichage.
                          const isPrimary = si === 0;
                          const titleClass = isPrimary ? "font-bold" : "font-medium";
                          // Vincent B861 (Eric 2026-02-14 SPEC STRICK PROD) —
                          // Badge « ✓ fusionnée » quand le timecode a été
                          // snappé au segment fusionné (source fiable
                          // cf. B860). On n'affiche AUCUN badge quand
                          // `whisper` pour ne pas alarmer inutilement
                          // l'utilisateur (défaut historique implicite).
                          const isMerged = src.transcript_source_used === "fusionnée";
                          const mergedBadge = isMerged ? (
                            <span
                              className="text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-semibold flex-shrink-0 inline-flex items-center gap-0.5"
                              data-testid={`source-merged-badge-${si}`}
                              title={lang === "fr" ? "Timecode calé sur la transcription fusionnée — extrait fiable" : "Timecode aligned on the merged transcript — reliable excerpt"}
                            >
                              <Check size={9} weight="bold" />
                              {lang === "fr" ? "fusionnée" : "merged"}
                            </span>
                          ) : null;
                          // If a Dropbox URL is configured for the video, send the user there
                          // with an HTML5 #t= fragment (browser MP4 viewer respects it).
                          if (src.dropbox_url) {
                            const sep = src.dropbox_url.includes("#") ? "" : (startSec != null ? `#t=${Math.floor(startSec)}` : "");
                            return (
                              <a key={si} href={`${src.dropbox_url}${sep}`} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-2 text-xs bg-white p-2 rounded-lg border ${isPrimary ? "border-teal-300 ring-1 ring-teal-100" : "border-teal-100"} hover:border-teal-400 hover:bg-teal-50 transition-colors`} data-testid={`source-link-${si}`} title="Ouvrir dans Dropbox (source officielle)">
                                <VideoCamera size={14} className="text-teal-600 flex-shrink-0" />
                                <span className={`${titleClass} text-teal-700 truncate`}>{src.video_title}</span>
                                {isPrimary && <span className="text-[9px] uppercase tracking-wider text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded font-semibold flex-shrink-0" data-testid={`source-primary-badge-${si}`}>{lang === "fr" ? "Principale" : "Primary"}</span>}
                                {mergedBadge}
                                {startSec != null && <span className="text-zinc-600 flex items-center gap-0.5 flex-shrink-0"><Clock size={10} /> {formatTs(startSec)}</span>}
                                <span className="ml-auto text-[10px] uppercase tracking-wider text-teal-700 font-semibold flex-shrink-0">Dropbox</span>
                              </a>
                            );
                          }
                          return (
                            <Link key={si} to={`/dashboard/videos/${src.video_id}${startSec != null ? `?t=${Math.floor(startSec)}` : ""}`} className={`flex items-center gap-2 text-xs bg-white p-2 rounded-lg border ${isPrimary ? "border-[#002FA7]/40 ring-1 ring-[#002FA7]/10" : "border-zinc-100"} hover:border-[#002FA7]/30 hover:bg-[#002FA7]/5 transition-colors`} data-testid={`source-link-${si}`}>
                              <VideoCamera size={14} className="text-[#002FA7] flex-shrink-0" />
                              <span className={`${titleClass} text-[#002FA7] truncate`}>{src.video_title}</span>
                              {isPrimary && <span className="text-[9px] uppercase tracking-wider text-[#002FA7] bg-[#002FA7]/10 px-1.5 py-0.5 rounded font-semibold flex-shrink-0" data-testid={`source-primary-badge-${si}`}>{lang === "fr" ? "Principale" : "Primary"}</span>}
                              {mergedBadge}
                              {startSec != null && <span className="text-zinc-600 flex items-center gap-0.5 flex-shrink-0"><Clock size={10} /> {formatTs(startSec)}</span>}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })()}
                  {msg.related_questions?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-zinc-200">
                      <p className="text-xs text-zinc-500 mb-2">{t("related_questions")} :</p>
                      <div className="space-y-1">
                        {msg.related_questions.map((q, qi) => (
                          <button key={qi} onClick={() => handleSubmit(q)} className="block w-full text-left text-xs text-[#002FA7] hover:underline p-1.5 hover:bg-zinc-50 rounded" data-testid={`related-question-${qi}`}>→ {q}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Vincent #260 (Eric 2026-02-06) — Bouton CTA "Générer un quiz"
                      après chaque réponse de l'assistant.
                      Bug 533 (Eric 2026-02-14) : n'utilise QUE des catégories vidéo
                      RÉELLES (content_categories de l'org), plus jamais le
                      free-form `training_theme` (qui pouvait valoir « sales » ou
                      « general » — catégories inexistantes dans le LMS FLHQ).
                      Vincent B607 (Eric 2026-07-29 SPEC STRICK) — Scope
                      strict : le quiz cible désormais D'ABORD la vidéo source
                      exacte (video_id), avec fallback sur le parcours qui
                      contient cette vidéo (pathway_id), et en tout dernier
                      recours la catégorie (category_id). Cela évite de
                      poser au user des questions issues d'autres modules
                      quand la réponse chatbot vient d'une vidéo précise. */}
                  {msg.role !== "user" && (msg.sources?.length > 0 || msg.content) && (() => {
                    const firstSource = msg.sources?.[0];
                    const sourceVideo = firstSource && videos.find(v => v.id === firstSource.video_id);
                    const realCategory = sourceVideo?.category_id
                      ? docCategories.find(c => c.id === sourceVideo.category_id)
                      : null;
                    // Vincent B607 : cherche un parcours qui contient cette vidéo (1er trouvé).
                    const sourcePathway = sourceVideo
                      ? (pathways || []).find(pw => (pw.video_ids || []).includes(sourceVideo.id))
                      : null;
                    // Priorité : video_id > pathway_id > category_id
                    let quizUrl = "/dashboard/quiz?auto=1";
                    let themeLabel = lang === "fr" ? "ce sujet" : "this topic";
                    if (sourceVideo) {
                      quizUrl = `/dashboard/quiz?video_id=${encodeURIComponent(sourceVideo.id)}&auto=1`;
                      themeLabel = sourceVideo.title || firstSource?.video_title || themeLabel;
                    } else if (sourcePathway) {
                      quizUrl = `/dashboard/quiz?pathway_id=${encodeURIComponent(sourcePathway.id)}&auto=1`;
                      themeLabel = sourcePathway.title || themeLabel;
                    } else if (realCategory) {
                      quizUrl = `/dashboard/quiz?category_id=${encodeURIComponent(realCategory.id)}&auto=1`;
                      themeLabel = realCategory.name;
                    }
                    const hasScope = Boolean(sourceVideo || sourcePathway || realCategory);
                    return (
                      <div className="mt-3 pt-3 border-t border-zinc-200">
                        <Link
                          to={quizUrl}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-violet-50 to-purple-50 text-violet-700 border border-violet-200 hover:border-violet-400 hover:bg-violet-100 transition-all font-semibold text-xs shadow-sm hover:shadow"
                          data-testid={`generate-quiz-link-${i}`}
                          title={lang === "fr" ? `Générer un quiz sur "${themeLabel}"` : `Generate a quiz on "${themeLabel}"`}
                        >
                          🎯 {lang === "fr"
                            ? `Tester mes connaissances sur ${hasScope ? `« ${themeLabel} »` : "ce sujet"}`
                            : `Test my knowledge on ${hasScope ? `"${themeLabel}"` : "this topic"}`}
                          <ArrowRight size={12} weight="bold" />
                        </Link>
                      </div>
                    );
                  })()}
                  {msg.role !== "user" && (
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => handleCopy(msg.content, i)} className="text-xs text-zinc-600 hover:text-zinc-800 flex items-center gap-1" data-testid={`copy-message-${i}`}>
                        {copiedId === i ? <><Check size={12} /> {t("copied")}</> : <><Copy size={12} /> {t("copy")}</>}
                      </button>
                      {msg.query_id && (
                        <div className="flex items-center gap-1 ml-auto relative" data-testid={`feedback-${i}`}>
                          {/* Vincent #509 (2026-07-21) — 2 boutons uniquement :
                              ThumbsUp (bonne réponse) + ThumbsDown (ouvre menu
                              de 7 raisons pour former le dialogueur). */}
                          {(() => {
                            const good = msg.feedback === "good";
                            const bad = msg.feedback && msg.feedback !== "good";
                            return (
                              <>
                                <button
                                  onClick={() => submitFeedback(i, "good")}
                                  disabled={!!msg.feedback}
                                  className={`p-1.5 rounded-md transition-colors ${
                                    msg.feedback && !good ? "text-zinc-300 cursor-not-allowed" :
                                    good ? "bg-teal-100 text-teal-700" :
                                    "text-zinc-400 hover:text-teal-600 hover:bg-teal-50"
                                  }`}
                                  title={lang === "fr" ? "Bonne réponse" : "Good answer"}
                                  data-testid={`feedback-good-${i}`}
                                >
                                  <ThumbsUp size={14} weight={good ? "fill" : "regular"} />
                                </button>
                                <button
                                  onClick={() => setFeedbackMenu(feedbackMenu?.msgIdx === i ? null : { msgIdx: i })}
                                  disabled={!!msg.feedback}
                                  className={`p-1.5 rounded-md transition-colors ${
                                    msg.feedback && !bad ? "text-zinc-300 cursor-not-allowed" :
                                    bad ? "bg-red-100 text-red-700" :
                                    "text-zinc-400 hover:text-red-600 hover:bg-red-50"
                                  }`}
                                  title={lang === "fr" ? "Réponse à améliorer" : "Response needs improvement"}
                                  data-testid={`feedback-bad-${i}`}
                                >
                                  <ThumbsDown size={14} weight={bad ? "fill" : "regular"} />
                                </button>
                              </>
                            );
                          })()}
                          {feedbackMenu?.msgIdx === i && !msg.feedback && (
                            <div
                              className="absolute right-0 top-full mt-2 w-96 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 p-3"
                              data-testid={`feedback-down-menu-${i}`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-semibold text-zinc-700">
                                  {lang === "fr" ? "Pourquoi la réponse est problématique ?" : "Why is this response problematic?"}
                                </p>
                                <button
                                  onClick={() => { setFeedbackMenu(null); setCustomReasonText(""); }}
                                  className="text-zinc-400 hover:text-zinc-600"
                                  data-testid={`feedback-menu-close-${i}`}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                              <div className="space-y-1 max-h-72 overflow-y-auto">
                                {FEEDBACK_DOWN_REASONS.map((r) => (
                                  <div key={r.id}>
                                    <button
                                      onClick={() => {
                                        if (r.id === "other") return; // gestion via champ commentaire
                                        submitFeedback(i, "negative", { reason: r.id });
                                        setFeedbackMenu(null);
                                      }}
                                      className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-50 transition-colors group"
                                      data-testid={`feedback-reason-${r.id}-${i}`}
                                    >
                                      <p className="text-xs font-medium text-zinc-800 group-hover:text-[#002FA7]">{r.label}</p>
                                      <p className="text-[10px] text-zinc-500 mt-0.5">{r.desc}</p>
                                    </button>
                                    {r.id === "other" && (
                                      <div className="px-2 mt-1 mb-1">
                                        <textarea
                                          value={customReasonText}
                                          onChange={(e) => setCustomReasonText(e.target.value)}
                                          placeholder={lang === "fr" ? "Précise ta raison…" : "Explain…"}
                                          className="w-full text-xs px-2 py-1.5 border border-zinc-300 rounded focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
                                          rows={2}
                                          data-testid={`feedback-other-text-${i}`}
                                        />
                                        <button
                                          onClick={() => {
                                            if (!customReasonText.trim()) return;
                                            submitFeedback(i, "negative", { reason: "other", comment: customReasonText.trim() });
                                            setFeedbackMenu(null);
                                            setCustomReasonText("");
                                          }}
                                          disabled={!customReasonText.trim()}
                                          className="mt-1 w-full text-xs px-2 py-1 bg-[#002FA7] text-white rounded hover:bg-blue-800 disabled:opacity-40 disabled:cursor-not-allowed"
                                          data-testid={`feedback-other-submit-${i}`}
                                        >
                                          {lang === "fr" ? "Envoyer" : "Send"}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {/* Vincent B606 optim D (Eric 2026-07-29 SPEC STRICK) — Ne
                pas afficher la bulle « Knoria réfléchit… » quand le mode
                streaming a déjà inséré une bulle placeholder assistante
                avec son curseur clignotant. Sinon 2 bulles se cumulent. */}
            {loading && !messages.some(m => m.role === "assistant" && m.streaming) && (
              <div className="flex items-start gap-3" data-testid="chat-loading-bubble">
                <div className="w-8 h-8 rounded-full bg-[#002FA7] flex items-center justify-center"><Robot size={16} className="text-white" /></div>
                <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-zinc-500"><SpinnerGap size={16} className="animate-spin" /><span className="text-sm">{t("thinking")}</span></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="mt-3 flex-shrink-0">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="relative">
          <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }} placeholder={t("ask_question")} rows={1} className="pr-24 resize-none rounded-xl min-h-[52px] max-h-40 text-sm" data-testid="chat-input" />
          <Button type="submit" disabled={loading || !input.trim()} className="absolute right-2 bottom-2 h-9 px-4 bg-[#002FA7] hover:bg-[#002280] rounded-lg" data-testid="send-message-btn">
            {loading ? <SpinnerGap size={18} className="animate-spin" /> : <PaperPlaneTilt size={18} weight="fill" />}
          </Button>
        </form>
        {fullscreen && <p className="text-[10px] text-center text-zinc-400 mt-1">Appuyez Echap pour quitter le plein ecran</p>}
      </div>
    </div>
  );
}
